var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { customPaymentMethods } from "@jti/fe-common";
import { getMessageBus } from "@jti/fe-message-bus";
import { magento } from "@jti/magento";
import { calculateProductPoints } from "@jti/rest-api";
import { fromMagentoCurrency } from "../../utils/fromMagentoCurrency";
import { parseAddToCartErrorMessage } from "../../utils/parseAddToCartErrorMessage";
import { placeOrder } from "../checkout/placeOrder";
import { addProductToCart } from "./actions/addProductToCart";
import { fetchCart } from "./actions/fetchCart";
import { fetchCartItems } from "./actions/fetchCartItems";
import { fetchCartPrices } from "./actions/fetchCartPrices";
const messageBus = getMessageBus();
export const removeItem = createAsyncThunk("cart/removeItem", ({ cartId, itemId }) => __awaiter(void 0, void 0, void 0, function* () { return magento.removeItemFromCart(cartId, itemId); }));
export const removeItems = createAsyncThunk("cart/removeItems", ({ cartId, itemIds }) => __awaiter(void 0, void 0, void 0, function* () { return magento.removeItemsFromCart(cartId, itemIds); }));
export const updateItemQuantity = createAsyncThunk("cart/updateItemQuantity", ({ cartId, itemId, quantity }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!!quantity || quantity === 0) {
        return magento.updateCartItemQuantity(cartId, itemId, quantity);
    }
}));
export const fetchCoupons = createAsyncThunk("cart/fetchCoupons", (cartId) => __awaiter(void 0, void 0, void 0, function* () { return magento.getCoupons(cartId); }));
export const applyCoupons = createAsyncThunk("cart/applyCoupons", ({ cartId, couponCode }) => __awaiter(void 0, void 0, void 0, function* () {
    const { appliedCoupons, messages, can_apply_coupons } = yield magento.applyCouponToCart(cartId, couponCode);
    return { appliedCoupons, can_apply_coupons, messages };
}));
export const removeAllCoupons = createAsyncThunk("cart/removeAllCoupons", (cartId) => __awaiter(void 0, void 0, void 0, function* () { return yield magento.removeCouponFromCart(cartId); }));
export const fetchCurrency = createAsyncThunk("cart/fetchCurrency", () => __awaiter(void 0, void 0, void 0, function* () { return magento.getCurrency(); }));
export const fetchStoreConfig = createAsyncThunk("cart/fetchStoreConfig", () => __awaiter(void 0, void 0, void 0, function* () { return magento.getStoreConfig(); }));
export const initialCartState = {
    id: "",
    items: [],
    oldItems: [],
    countries: [],
    prices: {
        fetchStatus: "idle",
        data: null,
    },
    isZeroValueOrder: false,
    isFailedPaymentFlow: false,
    isSubscription: false,
    minimumPayableAmount: undefined,
    coupons: [],
    couponsPending: false,
    couponsRemoveAllPending: false,
    availablePaymentMethods: [],
    itemsPending: true,
    qtyPending: false,
    notLoggedIn: false,
    couponErrors: [],
    order: {
        orderDetails: {
            fetchStatus: "idle",
            data: null,
        },
        points: 0,
    },
    currency: {
        code: "",
        symbol: "",
    },
    promoCode: {
        isVisible: true,
        isLimitReached: false,
    },
    storeConfig: {
        base_currency_code: "",
        is_order_limit_enabled: false,
        is_age_verification_enabled: false,
        order_limit_value: 0,
        store_code: "",
        store_group_code: "",
        is_free_gifts_enabled: false,
        gift_basket_quantity_limitation: "0",
        postcode_is_required: true,
        postcode_max_length: 0,
        postcode_min_length: 0,
        postcode_validate_regex: "",
        postcode_validation_enabled: false,
        allow_empty_firstname: false,
        cartons_limit_value: 0,
    },
    isLoadingCartData: false,
};
export const cartSlice = createSlice({
    name: "cart",
    initialState: initialCartState,
    reducers: {
        clearCouponErrors(state) {
            state.couponErrors = [];
        },
        setIsZeroValueOrder(state, action) {
            state.isZeroValueOrder = action.payload;
        },
        setIsFailedPaymentFlow(state, action) {
            state.isFailedPaymentFlow = action.payload;
            if (action.payload) {
                state.isZeroValueOrder = false;
            }
        },
    },
    extraReducers: (builder) => {
        builder.addCase(updateItemQuantity.pending, (state, action) => {
            state.qtyPending = true;
            state.oldItems = [...state.items];
            state.items = state.items.map((item) => item.id === action.meta.arg.itemId
                ? Object.assign(Object.assign({}, item), { quantity: action.meta.arg.quantity }) : item);
        });
        builder.addCase(updateItemQuantity.fulfilled, (state, action) => {
            state.qtyPending = false;
            if (action.meta.arg.quantity === 0) {
                state.items = state.items.filter((item) => item.id !== action.meta.arg.itemId);
            }
            state.items = state.items.map((item) => (Object.assign(Object.assign({}, item), { points: calculateProductPoints(item.rules, item.quantity) })));
        });
        builder.addCase(updateItemQuantity.rejected, (state) => {
            state.qtyPending = false;
            if (state.oldItems) {
                state.items = [...state.oldItems];
            }
        });
        builder.addCase(fetchCart.fulfilled, (state, action) => {
            var _a;
            state.id = action.payload.id;
            state.promoCode.isVisible = !!action.payload.can_apply_coupons;
            state.availablePaymentMethods = action.payload.available_payment_methods || [];
            messageBus.publish("cart.loaded", { payload: { cartId: (_a = action.payload.id) !== null && _a !== void 0 ? _a : "" } }, { sticky: true });
        });
        builder.addCase(fetchCart.rejected, (state) => {
            state.itemsPending = false;
            state.notLoggedIn = true;
        });
        builder.addCase(fetchCartPrices.rejected, (state) => {
            state.prices.fetchStatus = "error";
        });
        builder.addCase(fetchCartPrices.pending, (state) => {
            state.prices.fetchStatus = "pending";
        });
        builder.addCase(fetchCartPrices.fulfilled, (state, action) => {
            var _a, _b;
            state.prices.data = action.payload.prices;
            state.minimumPayableAmount = action.payload.minimum_payable_amount;
            if (state.minimumPayableAmount) {
                state.minimumPayableAmount.isReached = Boolean(state.minimumPayableAmount.value &&
                    ((_a = state.prices.data.grandTotal) === null || _a === void 0 ? void 0 : _a.value) &&
                    state.prices.data.grandTotal.value <= state.minimumPayableAmount.value);
            }
            if (!state.isFailedPaymentFlow) {
                state.availablePaymentMethods = (_b = action.payload.available_payment_methods) !== null && _b !== void 0 ? _b : [];
                state.isZeroValueOrder = state.availablePaymentMethods.some((method) => method.code === customPaymentMethods.zeroValueOrder);
            }
            state.prices.fetchStatus = "success";
        });
        builder.addCase(fetchCartItems.pending, (state) => {
            state.itemsPending = true;
        });
        builder.addCase(fetchCartItems.fulfilled, (state, action) => {
            state.items = action.payload.items;
            state.itemsPending = false;
            state.isSubscription = action.payload.items.filter((item) => item.subscription === 1).length > 0;
        });
        builder.addCase(fetchCartItems.rejected, (state) => {
            state.itemsPending = false;
        });
        builder.addCase(fetchCoupons.pending, (state) => {
            state.couponsPending = true;
        });
        builder.addCase(fetchCoupons.fulfilled, (state, action) => {
            var _a;
            const coupons = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.coupons;
            state.couponsPending = false;
            state.coupons = Array.isArray(coupons) ? coupons.filter((coupon) => coupon.code.length) : [];
        });
        builder.addCase(fetchCoupons.rejected, (state, action) => {
            state.couponsPending = false;
            state.couponErrors = [action.error.message || ""];
        });
        builder.addCase(applyCoupons.pending, (state) => {
            state.couponsPending = true;
            state.couponErrors = [];
        });
        builder.addCase(applyCoupons.fulfilled, (state, action) => {
            var _a;
            const coupons = (_a = action.payload) === null || _a === void 0 ? void 0 : _a.appliedCoupons;
            if (action.payload.messages.length) {
                state.couponErrors = action.payload.messages;
                state.couponsPending = false;
            }
            state.couponsPending = false;
            state.coupons = Array.isArray(coupons) ? coupons.filter((coupon) => coupon.code.length) : [];
            state.promoCode.isLimitReached = !action.payload.can_apply_coupons;
        });
        builder.addCase(applyCoupons.rejected, (state, action) => {
            state.couponsPending = false;
            state.couponErrors = [action.error.message || ""];
        });
        builder.addCase(removeAllCoupons.pending, (state) => {
            state.couponsRemoveAllPending = true;
            state.couponErrors = [];
        });
        builder.addCase(removeAllCoupons.fulfilled, (state) => {
            state.couponsRemoveAllPending = false;
        });
        builder.addCase(placeOrder.fulfilled, (state) => {
            // state.page = "order";
            state.items = [];
        });
        builder.addCase(fetchCurrency.fulfilled, (state, action) => {
            state.currency = fromMagentoCurrency(action.payload);
        });
        builder.addCase(fetchStoreConfig.fulfilled, (state, action) => {
            state.storeConfig = action.payload;
        });
        builder.addCase(addProductToCart.fulfilled, () => {
            messageBus.publish("success.cart.add", { payload: {} });
        });
        builder.addCase(addProductToCart.rejected, (state, action) => {
            action.payload &&
                messageBus.publish("fail.cart.add", {
                    payload: { message: parseAddToCartErrorMessage(action.payload) },
                });
        });
    },
});
export const { clearCouponErrors, setIsZeroValueOrder, setIsFailedPaymentFlow } = cartSlice.actions;
//# sourceMappingURL=cartSlice.js.map